import React from 'react';
import { motion } from 'framer-motion';
import { Zap, AlertCircle, Gift } from 'lucide-react';
import { Button } from './ui/button';

const UsageBanner = ({ usage, onUpgrade }) => {
  if (!usage) return null;

  const { papers_used, papers_limit, is_unlimited, plan_name, referral_bonus } = usage;
  
  // Don't show for unlimited plans
  if (is_unlimited) return null;
  
  const percentUsed = (papers_used / papers_limit) * 100;
  const isNearLimit = percentUsed >= 80;
  const isAtLimit = papers_used >= papers_limit;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        mx-6 mt-4 p-4 rounded-xl border flex items-center justify-between
        ${isAtLimit 
          ? 'bg-red-500/10 border-red-500/30' 
          : isNearLimit 
            ? 'bg-orange-500/10 border-orange-500/30'
            : 'bg-slate-800/30 border-slate-700/50'
        }
      `}
      data-testid="usage-banner"
    >
      <div className="flex items-center gap-3">
        {isAtLimit ? (
          <AlertCircle className="w-5 h-5 text-red-400" />
        ) : (
          <Zap className="w-5 h-5 text-teal-400" />
        )}
        <div>
          <p className="text-sm font-medium text-white">
            {isAtLimit 
              ? 'You\'ve reached your limit' 
              : `${papers_used} of ${papers_limit} papers used`
            }
          </p>
          <p className="text-xs text-slate-400">
            {plan_name} plan • Resets monthly
            {referral_bonus > 0 && (
              <span className="ml-2 text-teal-400">
                <Gift className="w-3 h-3 inline mr-1" />
                +{referral_bonus} bonus
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-4">
        <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percentUsed, 100)}%` }}
            className={`h-full rounded-full ${
              isAtLimit 
                ? 'bg-red-500' 
                : isNearLimit 
                  ? 'bg-orange-500' 
                  : 'bg-teal-500'
            }`}
          />
        </div>
        
        <Button
          onClick={onUpgrade}
          size="sm"
          data-testid="upgrade-from-banner"
          className="bg-teal-500 hover:bg-teal-400 text-slate-900 text-xs"
        >
          Upgrade
        </Button>
      </div>
    </motion.div>
  );
};

export default UsageBanner;
